<script setup lang="ts">
import {
  PlayerAccessRequiredError,
  PlayerSessionUnauthorizedError,
  playerApi,
  type PlayerChatCapabilities,
  type PlayerChatMessage,
  type PlayerMatchmakerState,
  type PlayerMe,
  type PlayerCommunicationRestrictions,
  type PlayerParty,
  type PlayerPartyInvite,
} from '@/api/public'
import PlayerChat from '@/components/player/PlayerChat.vue'
import PartyStrip from '@/components/player/PartyStrip.vue'
import PlayerAddFriendDialog from '@/components/player/PlayerAddFriendDialog.vue'
import PlayerFriendsPanel from '@/components/player/PlayerFriendsPanel.vue'
import PlayerNotificationCenter from '@/components/player/PlayerNotificationCenter.vue'
import PlayerPartyInviteDialog from '@/components/player/PlayerPartyInviteDialog.vue'
import PlayerPartyPanel from '@/components/player/PlayerPartyPanel.vue'
import PlayerVerificationNotice from '@/components/player/PlayerVerificationNotice.vue'
import {
  collapsedPlayerChatUnreadCount,
  focusTrapDestination,
  hasGlobalPlayerChatUnread,
  isValidFriendSteamID,
  partyMembershipKey,
  playerDockErrorMessage,
  sortSocialFriends,
  togglePlayerDockSurface,
  type PlayerDockSurface,
  type PlayerSocialStatusPresentation,
} from '@/lib/playerSocialPresentation'
import {
  isPartyConcurrencyError,
  validatePlayerPartyVersion,
} from '@/lib/playerPartyVersion'
import {
  playerProfileIgnoreAction,
  playerProfilePartyInvitePresentation,
  playerProfileRelationship,
  playerProfileSocialStatus,
  unavailablePlayerProfileSocialActionState,
  type PlayerProfileFriendAction,
  type PlayerProfileIgnoreAction,
  type PlayerProfileSocialActionsController,
  type PlayerProfileSocialActionState,
} from '@/lib/playerProfileSocialActions'
import {
  playerPartyRosterLockMessage,
  type PlayerPartyRosterLockReason,
} from '@/lib/playerPartyRosterLock'
import {
  dispatchIncomingChatSoundOnce,
  dispatchIncomingSocialSoundOnce,
  preparePlayerNotificationAudio,
} from '@/lib/readyCheckAlert'
import { usePlayerSocialDock } from '@/lib/usePlayerSocialDock'
import { useRouter } from 'vue-router'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'

const props = defineProps<{
  authenticated: boolean
  accessAllowed: boolean
  verificationContactUrl?: string | null
  player: PlayerMe | null
  chatCapabilities: PlayerChatCapabilities | null
  communicationRestrictions?: PlayerCommunicationRestrictions | null
  hidden: boolean
  externalSurfaceOpen: boolean
  navigationSurfaceOpen: boolean
  matchmakerStatus: PlayerMatchmakerState['status']
  partyRosterLocked: boolean
  partyRosterLockReason: PlayerPartyRosterLockReason | null
  matchmakerBusy: boolean
}>()

const emit = defineEmits<{
  unauthorized: []
  accessRequired: []
  partyChanged: []
  communicationPolicyChanged: [observedAt: number]
  partyState: [party: PlayerParty | null]
  closeExternalSurface: []
  partyBusy: [busy: boolean]
  dockModal: [open: boolean]
}>()

const router = useRouter()
const surface = ref<PlayerDockSurface>('none')
const friendRequestDraft = ref('')
const chatDraft = ref('')
const recoveredDraftSourceTargetKey = ref<string | null>(null)
const selectedPartyMemberSteamID = ref('')
const profileFeedbackSteamID = ref('')
const profileActionError = ref<string | null>(null)
const dockRoot = ref<HTMLElement | null>(null)
const playerChat = ref<{
  dismissOverlay: () => boolean
  focusComposer: () => void
} | null>(null)
const addFriendDialogOpen = ref(false)
const restrictedPartyCleanup = ref<{
  actorSteamID: string
  etag: string
  partyID: string
  leader: boolean
} | null>(null)
const restrictedPartyCleanupLoading = ref(false)
const restrictedPartyCleanupPending = ref(false)
const restrictedPartyCleanupError = ref<string | null>(null)
const partyInviteCooldownUntil = ref<Record<string, number>>({})
const dismissedPartyInviteIDs = ref<Set<string>>(new Set())
const cooldownClock = ref(Date.now())
let addFriendTrigger: HTMLElement | null = null
let returnFocusTarget: HTMLElement | null = null
let partyInviteReturnFocus: HTMLElement | null = null
let dockResizeObserver: ResizeObserver | null = null
let cooldownTimer: number | undefined
let restrictedPartyCleanupLoadRequestID = 0
let restrictedPartyCleanupActionRequestID = 0

const resolvedChatCapabilities = computed<PlayerChatCapabilities>(() => (
  props.chatCapabilities ?? {
    global: false,
    party: false,
    dm: false,
  }
))
const runtime = usePlayerSocialDock({
  authenticated: () => props.authenticated && props.accessAllowed,
  steamID: () => props.player?.steam_id ?? '',
  chatCapabilities: () => resolvedChatCapabilities.value,
  onUnauthorized: () => emit('unauthorized'),
  onAccessRequired: () => emit('accessRequired'),
  onPartyChanged: () => emit('partyChanged'),
  onCommunicationPolicyChanged: observedAt => emit('communicationPolicyChanged', observedAt),
  onIncomingChatMessage: dispatchIncomingChatSoundOnce,
  onIncomingSocialNotification: dispatchIncomingSocialSoundOnce,
})
const visibleIncomingPartyInvites = computed(() => {
  const socialState = runtime.socialState.value
  if (!socialState) return []
  const ignoredSteamIDs = new Set(
    (socialState.notification_ignores ?? []).map(ignore => ignore.player.steam_id),
  )
  return runtime.incomingPartyInvites.value.filter(
    invite => !ignoredSteamIDs.has(invite.invited_by_steam_id),
  )
})
const activePartyInvite = computed(() => (
  visibleIncomingPartyInvites.value.find(
    invite => !dismissedPartyInviteIDs.value.has(invite.id),
  ) ?? null
))
const dockModalOpen = computed(() => (
  Boolean(activePartyInvite.value) ||
  addFriendDialogOpen.value ||
  surface.value === 'friends' ||
  surface.value === 'party'
))
const sortedFriends = computed(() => sortSocialFriends(runtime.socialState.value?.friends ?? []))
const selectedPartyMemberMessageState = computed(() => {
  const steamID = selectedPartyMemberSteamID.value
  if (!steamID) {
    return {
      enabled: false,
      reason: '',
    }
  }
  if (runtime.socialLoading.value || !runtime.socialState.value) {
    return {
      enabled: false,
      reason: 'Список друзей синхронизируется.',
    }
  }
  if (!resolvedChatCapabilities.value.dm) {
    return {
      enabled: false,
      reason: 'Личные сообщения недоступны.',
    }
  }
  if (!sortedFriends.value.some(friend => friend.player.steam_id === steamID)) {
    return {
      enabled: false,
      reason: 'Личные сообщения доступны только друзьям.',
    }
  }
  if (runtime.socialActionPending.value) {
    return {
      enabled: false,
      reason: 'Дождитесь завершения социального действия.',
    }
  }
  return {
    enabled: true,
    reason: '',
  }
})
const incomingFriendRequests = computed(() => runtime.socialState.value?.incoming_requests ?? [])
const outgoingFriendRequests = computed(() => runtime.socialState.value?.outgoing_requests ?? [])
const groupUnreadCount = computed(() => (
  runtime.conversations.value.find(conversation => conversation.kind === 'party')
    ?.unread_count ?? 0
))
const collapsedChatUnreadCount = computed(() => (
  collapsedPlayerChatUnreadCount(runtime.conversations.value)
))
const globalChatHasUnread = computed(() => (
  resolvedChatCapabilities.value.global &&
  hasGlobalPlayerChatUnread(runtime.conversations.value)
))
const activePartyInviteStale = computed(() => {
  const invite = activePartyInvite.value
  return Boolean(
    invite &&
    invite.party.revision !== invite.created_at_revision,
  )
})
const partyInviteCooldowns = computed<Record<string, number>>(() => {
  const now = cooldownClock.value
  return Object.fromEntries(
    Object.entries(partyInviteCooldownUntil.value)
      .map(([steamID, readyAt]) => [
        steamID,
        Math.max(0, Math.ceil((readyAt - now) / 1000)),
      ] as const)
      .filter(([, seconds]) => seconds > 0),
  )
})
const rosterLocked = computed(() => (
  props.matchmakerBusy ||
  (props.accessAllowed && !runtime.partyStateHealthy.value) ||
  props.partyRosterLocked
))
const rosterLockReason = computed(() => {
  if (props.matchmakerBusy) return 'Дождитесь завершения действия с поиском.'
  if (props.accessAllowed && !runtime.partyStateHealthy.value) {
    return 'Состав группы не синхронизирован. Повторяем подключение.'
  }
  if (props.partyRosterLocked) {
    return playerPartyRosterLockMessage(props.partyRosterLockReason)
  }
  return ''
})
const replacingOwnedSingleton = computed(() => {
  const party = runtime.party.value
  const actorSteamID = props.player?.steam_id ?? ''
  return Boolean(
    party &&
    actorSteamID &&
    party.leader_steam_id === actorSteamID &&
    party.members.length === 1 &&
    party.members[0]?.steam_id === actorSteamID,
  )
})
const currentPartyInviteReplacementNotice = computed(() => (
  replacingOwnedSingleton.value
    ? 'Текущая одиночная группа, её чат и отправленные из неё приглашения будут закрыты.'
    : ''
))
const currentPartyInviteConflictReason = computed(() => {
  const party = runtime.party.value
  const actorSteamID = props.player?.steam_id ?? ''
  if (!party || !actorSteamID || replacingOwnedSingleton.value) return ''
  if (party.leader_steam_id === actorSteamID) {
    return 'Сначала распустите текущую группу или передайте лидерство и покиньте её, затем вернитесь к приглашению.'
  }
  return 'Сначала покиньте текущую группу, затем вернитесь к приглашению.'
})
const partyInviteAcceptDisabled = computed(() => (
  rosterLocked.value || Boolean(currentPartyInviteConflictReason.value) || activePartyInviteStale.value
))
const partyInviteAcceptDisabledReason = computed(() => {
  if (rosterLockReason.value) return rosterLockReason.value
  if (currentPartyInviteConflictReason.value) return currentPartyInviteConflictReason.value
  if (activePartyInviteStale.value) {
    return 'Состав группы изменился после приглашения. Отклоните его и попросите новое.'
  }
  return ''
})
const partyBusy = computed(() => (
  runtime.partyLoading.value ||
  runtime.partyActionPending.value ||
  restrictedPartyCleanupLoading.value ||
  restrictedPartyCleanupPending.value ||
  (props.accessAllowed && !runtime.partyStateHealthy.value)
))
const restrictedPartyCleanupLabel = computed(() => {
  const cleanup = restrictedPartyCleanup.value
  if (!cleanup || props.accessAllowed) return ''
  return cleanup.leader ? 'Распустить группу' : 'Покинуть группу'
})
const restrictedPartyCleanupBusy = computed(() => (
  restrictedPartyCleanupLoading.value ||
  restrictedPartyCleanupPending.value
))

watch(runtime.party, party => emit('partyState', party), { immediate: true })
watch(
  [
    selectedPartyMemberSteamID,
    () => props.player?.steam_id ?? '',
    () => partyMembershipKey(
      runtime.party.value,
      selectedPartyMemberSteamID.value,
    ),
  ],
  ([selectedSteamID, currentSteamID, membershipKey], previous) => {
    if (!selectedSteamID) return
    const previousSelectedSteamID = previous?.[0] ?? ''
    const previousMembershipKey = previous?.[2] ?? ''
    const membershipIdentityChanged = Boolean(
      previousSelectedSteamID === selectedSteamID &&
      previousMembershipKey &&
      membershipKey !== previousMembershipKey,
    )
    if (
      selectedSteamID === currentSteamID ||
      membershipIdentityChanged ||
      !membershipKey
    ) {
      selectedPartyMemberSteamID.value = ''
      if (surface.value === 'party') {
        void nextTick(() => {
          document.getElementById('player-party-panel')?.focus()
        })
      }
    }
  },
)
watch(partyBusy, busy => emit('partyBusy', busy), {
  immediate: true,
  flush: 'sync',
})
watch(dockModalOpen, open => emit('dockModal', open), {
  immediate: true,
  flush: 'sync',
})
watch(
  [() => props.authenticated, () => props.accessAllowed, () => props.player?.steam_id ?? ''],
  ([authenticated, accessAllowed, steamID], previous) => {
    const previousSteamID = previous?.[2] ?? ''
    if (
      !authenticated ||
      !accessAllowed ||
      !steamID ||
      (previousSteamID && previousSteamID !== steamID)
    ) {
      closeSurface(false)
      friendRequestDraft.value = ''
      chatDraft.value = ''
      recoveredDraftSourceTargetKey.value = null
      runtime.clearFailedSendDraftRecovery()
      profileFeedbackSteamID.value = ''
      profileActionError.value = null
      partyInviteCooldownUntil.value = {}
      dismissedPartyInviteIDs.value = new Set()
      clearCooldownTimer()
      returnFocusTarget = null
      partyInviteReturnFocus = null
    }
  },
  { immediate: true },
)
watch(
  [
    () => props.authenticated,
    () => props.accessAllowed,
    () => props.player?.steam_id ?? '',
    () => runtime.party.value,
    () => runtime.partyETag.value,
    () => runtime.partyStateHealthy.value,
  ],
  ([authenticated, accessAllowed, steamID, party, etag, healthy]) => {
    if (!authenticated || !steamID || !accessAllowed) return
    if (party && etag) {
      restrictedPartyCleanup.value = restrictedPartyCleanupSnapshot(
        party,
        etag,
        steamID,
      )
      return
    }
    if (healthy) restrictedPartyCleanup.value = null
  },
  { immediate: true },
)
watch(
  [() => props.authenticated, () => props.accessAllowed, () => props.player?.steam_id ?? ''],
  ([authenticated, accessAllowed, steamID], previous) => {
    const previousSteamID = previous?.[2] ?? ''
    if (
      !authenticated ||
      !steamID ||
      (previousSteamID && previousSteamID !== steamID) ||
      accessAllowed
    ) {
      resetRestrictedPartyCleanup()
      return
    }
    void loadRestrictedPartyCleanup()
  },
  { immediate: true },
)
watch(runtime.activeChatTargetKey, (target, previousTarget) => {
  if (!previousTarget || target === previousTarget) return
  if (
    runtime.failedSendDraftTargetKey.value === previousTarget &&
    chatDraft.value
  ) {
    recoveredDraftSourceTargetKey.value = previousTarget
    runtime.clearFailedSendDraftRecovery(previousTarget)
    return
  }
  recoveredDraftSourceTargetKey.value = null
  chatDraft.value = ''
})
watch(
  () => runtime.incomingPartyInvites.value.map(invite => invite.id).join('\u0000'),
  () => {
    if (dismissedPartyInviteIDs.value.size === 0) return
    const currentIDs = new Set(runtime.incomingPartyInvites.value.map(invite => invite.id))
    dismissedPartyInviteIDs.value = new Set(
      [...dismissedPartyInviteIDs.value].filter(inviteID => currentIDs.has(inviteID)),
    )
  },
)
watch(() => props.hidden, hidden => {
  if (hidden) {
    closeSurface(false)
    return
  }
  if (activePartyInvite.value) void focusPartyInviteDialog()
})
watch(() => props.externalSurfaceOpen, open => {
  if (open) closeSurface(false)
})
watch(
  () => activePartyInvite.value?.id ?? '',
  async (inviteID, previousInviteID) => {
    if (!inviteID) {
      if (!previousInviteID) return
      const focusTarget = partyInviteReturnFocus
      partyInviteReturnFocus = null
      await nextTick()
      restorePartyInviteFocus(focusTarget)
      return
    }
    if (!previousInviteID) {
      const activeElement = document.activeElement
      partyInviteReturnFocus = activeElement instanceof HTMLElement
        ? activeElement
        : null
    }
    runtime.clearPartyError()
    runtime.clearSocialError()
    closeSurface(false)
    if (props.externalSurfaceOpen) emit('closeExternalSurface')
    await focusPartyInviteDialog()
  },
)
onMounted(() => {
  window.addEventListener('keydown', handleWindowKeydown)
  window.addEventListener('pointerdown', handleWindowPointerDown)
  window.addEventListener('resize', measureDock)
  if (typeof ResizeObserver !== 'undefined') {
    dockResizeObserver = new ResizeObserver(measureDock)
    if (dockRoot.value) dockResizeObserver.observe(dockRoot.value)
  }
  void nextTick(measureDock)
})

onBeforeUnmount(() => {
  resetRestrictedPartyCleanup()
  window.removeEventListener('keydown', handleWindowKeydown)
  window.removeEventListener('pointerdown', handleWindowPointerDown)
  window.removeEventListener('resize', measureDock)
  dockResizeObserver?.disconnect()
  dockResizeObserver = null
  clearCooldownTimer()
  document.documentElement.style.removeProperty('--player-dock-measured-height')
  emit('dockModal', false)
})

function measureDock(): void {
  const root = dockRoot.value
  if (!root) return
  const rootRect = root.getBoundingClientRect()
  if (rootRect.height <= 0) return
  document.documentElement.style.setProperty(
    '--player-dock-measured-height',
    `${Math.ceil(rootRect.height)}px`,
  )
  const composer = root.querySelector<HTMLElement>('.player-chat-composer')
  if (!composer) return
  const navBottom = document.querySelector<HTMLElement>('.dnav')
    ?.getBoundingClientRect().bottom ?? (window.innerWidth <= 760 ? 56 : 63)
  const availableHeight = Math.max(
    0,
    Math.floor(composer.getBoundingClientRect().top - navBottom - 18),
  )
  root.style.setProperty('--player-dock-chat-available-height', `${availableHeight}px`)
}

function restrictedPartyCleanupSnapshot(
  party: PlayerParty,
  etag: string,
  actorSteamID: string,
) {
  return {
    actorSteamID,
    etag,
    partyID: party.id,
    leader: party.leader_steam_id === actorSteamID,
  }
}

function resetRestrictedPartyCleanup(): void {
  restrictedPartyCleanupLoadRequestID += 1
  restrictedPartyCleanupActionRequestID += 1
  restrictedPartyCleanup.value = null
  restrictedPartyCleanupLoading.value = false
  restrictedPartyCleanupPending.value = false
  restrictedPartyCleanupError.value = null
}

async function loadRestrictedPartyCleanup(): Promise<void> {
  const actorSteamID = props.player?.steam_id ?? ''
  if (!props.authenticated || props.accessAllowed || !actorSteamID) return
  const requestID = ++restrictedPartyCleanupLoadRequestID
  restrictedPartyCleanupLoading.value = true
  restrictedPartyCleanupError.value = null
  try {
    const snapshot = await playerApi.partyState()
    if (
      requestID !== restrictedPartyCleanupLoadRequestID ||
      props.accessAllowed ||
      actorSteamID !== props.player?.steam_id
    ) {
      return
    }
    const party = snapshot.state.party
    const version = validatePlayerPartyVersion(party, {
      headerETag: snapshot.etag,
      jsonETag: snapshot.state.party_etag,
    })
    restrictedPartyCleanup.value = party && version.valid && version.version
      ? restrictedPartyCleanupSnapshot(
          party,
          version.version.etag,
          actorSteamID,
        )
      : null
    if (party && !version.valid) {
      restrictedPartyCleanupError.value =
        'Не удалось проверить версию состава. Обновите данные и повторите действие.'
    }
  } catch (error) {
    if (requestID !== restrictedPartyCleanupLoadRequestID) return
    if (error instanceof PlayerSessionUnauthorizedError) {
      emit('unauthorized')
      return
    }
    if (error instanceof PlayerAccessRequiredError) {
      // Older servers can still gate this read. A snapshot retained before
      // revocation remains usable for cleanup.
      return
    }
    restrictedPartyCleanupError.value = playerDockErrorMessage(error)
  } finally {
    if (requestID === restrictedPartyCleanupLoadRequestID) {
      restrictedPartyCleanupLoading.value = false
    }
  }
}

async function cleanupRestrictedParty(): Promise<void> {
  const cleanup = restrictedPartyCleanup.value
  if (!cleanup || restrictedPartyCleanupBusy.value || props.accessAllowed) return
  const prompt = cleanup.leader
    ? 'Распустить текущую группу? Это действие нельзя отменить.'
    : 'Покинуть текущую группу?'
  if (!window.confirm(prompt)) return

  const requestID = ++restrictedPartyCleanupActionRequestID
  restrictedPartyCleanupPending.value = true
  restrictedPartyCleanupError.value = null
  try {
    if (cleanup.leader) {
      await playerApi.disbandParty(cleanup.etag)
    } else {
      await playerApi.leaveParty(cleanup.etag)
    }
    if (requestID !== restrictedPartyCleanupActionRequestID) return
    restrictedPartyCleanup.value = null
    emit('partyState', null)
    emit('partyChanged')
  } catch (error) {
    if (requestID !== restrictedPartyCleanupActionRequestID) return
    if (error instanceof PlayerSessionUnauthorizedError) {
      emit('unauthorized')
      return
    }
    if (isPartyConcurrencyError(error)) {
      await loadRestrictedPartyCleanup()
      restrictedPartyCleanupError.value = 'Состав группы изменился. Проверьте состояние и повторите действие.'
      return
    }
    restrictedPartyCleanupError.value = playerDockErrorMessage(error)
  } finally {
    if (requestID === restrictedPartyCleanupActionRequestID) {
      restrictedPartyCleanupPending.value = false
    }
  }
}

function toggleSurface(
  requested: Exclude<PlayerDockSurface, 'none'>,
  trigger?: HTMLElement | null,
): void {
  if (addFriendDialogOpen.value) closeAddFriendDialog(false, true)
  if (props.externalSurfaceOpen) {
    emit('closeExternalSurface')
  }
  if (requested !== 'party') selectedPartyMemberSteamID.value = ''
  if (trigger) returnFocusTarget = trigger
  surface.value = togglePlayerDockSurface(surface.value, requested)
  if (surface.value === 'none') {
    returnFocusTarget = null
    return
  }
  if (surface.value === 'friends') {
    runtime.clearSocialError()
    runtime.clearPartyError()
    if (!runtime.partyStateHealthy.value) void runtime.refreshParty()
  } else if (surface.value === 'party') {
    runtime.clearPartyError()
    if (!runtime.partyStateHealthy.value) void runtime.refreshParty()
  }
  if (surface.value === 'chat') {
    void runtime.markLatestMessageRead()
    return
  }
  void focusSurface(surface.value)
}

function openPartyMember(steamID: string, trigger: HTMLElement | null): void {
  const party = runtime.party.value
  if (
    !steamID ||
    !party?.members.some(member => member.steam_id === steamID)
  ) {
    return
  }
  if (steamID === props.player?.steam_id) {
    selectedPartyMemberSteamID.value = ''
    if (surface.value === 'party') {
      void focusSurface('party')
      return
    }
    toggleSurface('party', trigger)
    return
  }
  selectedPartyMemberSteamID.value = steamID
  if (trigger) returnFocusTarget = trigger
  if (surface.value === 'party') {
    void focusSelectedPartyMemberControl()
    return
  }
  toggleSurface('party', trigger)
}

function selectPartyMember(steamID: string): void {
  if (
    !steamID ||
    steamID === props.player?.steam_id ||
    !runtime.party.value?.members.some(member => member.steam_id === steamID)
  ) {
    return
  }
  selectedPartyMemberSteamID.value = steamID
  const slot = dockRoot.value?.querySelector<HTMLElement>(
    `.party-strip-slot[data-party-member-steam-id="${steamID}"]`,
  )
  if (slot) returnFocusTarget = slot
  void focusSelectedPartyMemberControl()
}

function openFriendsSurface(trigger: HTMLElement | null = null): void {
  if (surface.value === 'friends') return
  toggleSurface('friends', trigger)
}

function openPlayerProfile(steamID: string): void {
  if (!steamID) return
  if (props.externalSurfaceOpen) emit('closeExternalSurface')
  closeSurface(false)
  void router.push({
    name: 'player-profile',
    params: { steam_id: steamID },
  })
}

function closeSurface(restoreFocus = true): void {
  if (surface.value === 'none' && !addFriendDialogOpen.value) return
  closeAddFriendDialog(false, true)
  const focusTarget = returnFocusTarget
  returnFocusTarget = null
  selectedPartyMemberSteamID.value = ''
  surface.value = 'none'
  if (restoreFocus && focusTarget) {
    void nextTick(() => {
      if (focusTarget.isConnected) focusTarget.focus()
    })
  }
}

async function focusSurface(nextSurface: PlayerDockSurface): Promise<void> {
  await nextTick()
  const id = nextSurface === 'friends'
    ? 'player-friends-panel'
    : nextSurface === 'party'
      ? 'player-party-panel'
      : ''
  if (!id) return
  const panel = document.getElementById(id)
  if (
    nextSurface === 'party' &&
    selectedPartyMemberSteamID.value
  ) {
    const primaryAction = selectedPartyMemberFocusTarget(panel)
    if (primaryAction) {
      primaryAction.focus()
      return
    }
  }
  panel?.focus()
}

function selectedPartyMemberFocusTarget(panel: ParentNode | null): HTMLElement | null {
  return (
    panel?.querySelector<HTMLElement>(
      '.party-member-action:not(:disabled):not([aria-disabled="true"])',
    ) ??
    panel?.querySelector<HTMLElement>('[data-party-member-primary-action]') ??
    null
  )
}

async function focusSelectedPartyMemberControl(): Promise<void> {
  await nextTick()
  if (surface.value !== 'party' || !selectedPartyMemberSteamID.value) return
  selectedPartyMemberFocusTarget(
    document.getElementById('player-party-panel'),
  )?.focus()
}

function handleWindowKeydown(event: KeyboardEvent): void {
  if (event.isTrusted) preparePlayerNotificationAudio()
  if (activePartyInvite.value && !props.hidden) {
    if (event.key === 'Tab') {
      trapFocusWithin(event, 'player-party-invite-dialog')
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      dismissPartyInvite(activePartyInvite.value.id)
    }
    return
  }
  if (addFriendDialogOpen.value) {
    if (event.key === 'Tab') {
      trapFocusWithin(event, 'player-add-friend-dialog')
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      if (runtime.socialActionPending.value) return
      closeAddFriendDialog()
    }
    return
  }
  if (
    event.key === 'Tab' &&
    (surface.value === 'friends' || surface.value === 'party')
  ) {
    trapFocusWithin(
      event,
      surface.value === 'friends' ? 'player-friends-panel' : 'player-party-panel',
    )
    return
  }
  if (
    event.key === 'Escape' &&
    surface.value === 'chat' &&
    playerChat.value?.dismissOverlay()
  ) {
    event.preventDefault()
    return
  }
  if (event.key === 'Escape' && surface.value !== 'none') {
    event.preventDefault()
    closeSurface()
  }
}

function trapFocusWithin(
  event: KeyboardEvent,
  containerID: string,
): void {
  const panel = document.getElementById(containerID)
  if (!panel) return
  const focusable = [...panel.querySelectorAll<HTMLElement>(
    'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
  )].filter(element => {
    if (element.closest('[inert]')) return false
    const style = window.getComputedStyle(element)
    return style.visibility !== 'hidden' && style.display !== 'none'
  })
  if (focusable.length === 0) {
    event.preventDefault()
    panel.focus()
    return
  }
  const active = document.activeElement
  const activeIndex = active instanceof HTMLElement
    ? focusable.indexOf(active)
    : -1
  const destination = focusTrapDestination(
    activeIndex,
    focusable.length,
    event.shiftKey,
  )
  if (destination === null) return
  event.preventDefault()
  focusable[destination].focus()
}

async function focusPartyInviteDialog(): Promise<void> {
  await nextTick()
  if (props.hidden || !activePartyInvite.value) return
  const dialog = document.getElementById('player-party-invite-dialog')
  const close = dialog?.querySelector<HTMLButtonElement>(
    '.player-party-invite-close:not(:disabled)',
  )
  if (close) {
    close.focus({ preventScroll: true })
  } else {
    dialog?.focus({ preventScroll: true })
  }
}

function restorePartyInviteFocus(preferred: HTMLElement | null): void {
  if (
    preferred?.isConnected &&
    preferred !== document.body &&
    preferred !== document.documentElement &&
    !preferred.closest('[inert]')
  ) {
    preferred.focus({ preventScroll: true })
    return
  }
  const fallback = document.querySelector<HTMLElement>(
    '.party-strip-slot:not(:disabled), .player-notification-trigger:not(:disabled), .find-match:not(:disabled), .dnav-brand-link',
  )
  if (fallback?.isConnected && !fallback.closest('[inert]')) {
    fallback.focus({ preventScroll: true })
  }
}

function handleWindowPointerDown(event: PointerEvent): void {
  if (event.isTrusted) preparePlayerNotificationAudio()
  if (surface.value === 'none') return
  const target = event.target
  if (!(target instanceof Node) || dockRoot.value?.contains(target)) return
  closeSurface(false)
}

async function submitFriendRequest(): Promise<void> {
  const steamID = friendRequestDraft.value.trim()
  if (!isValidFriendSteamID(steamID, props.player?.steam_id ?? '')) return
  if (await runtime.sendFriendRequest(steamID)) {
    closeAddFriendDialog()
  }
}

function openAddFriendDialog(trigger: HTMLElement | null): void {
  if (surface.value !== 'friends' || runtime.socialActionPending.value) return
  runtime.clearSocialError()
  addFriendTrigger = trigger
  addFriendDialogOpen.value = true
}

function closeAddFriendDialog(restoreFocus = true, force = false): void {
  if (!addFriendDialogOpen.value && !addFriendTrigger) return
  if (runtime.socialActionPending.value && !force) return
  const focusTarget = addFriendTrigger
  addFriendTrigger = null
  addFriendDialogOpen.value = false
  friendRequestDraft.value = ''
  runtime.clearSocialError()
  if (restoreFocus && focusTarget) {
    void nextTick(() => {
      if (focusTarget.isConnected) focusTarget.focus()
    })
  }
}

async function inviteFriend(steamID: string): Promise<boolean> {
  if (rosterLocked.value || (partyInviteCooldowns.value[steamID] ?? 0) > 0) return false
  if (!confirmLogicalSoloInviteDuringSearch()) return false
  const invited = await runtime.inviteFriendToParty(steamID)
  const retry = runtime.partyInviteCooldownRetry.value
  const cooldownSeconds = invited
    ? 10
    : retry?.steamID === steamID
      ? retry.seconds
      : 0
  if (cooldownSeconds <= 0) return invited
  const now = Date.now()
  cooldownClock.value = now
  partyInviteCooldownUntil.value = {
    ...partyInviteCooldownUntil.value,
    [steamID]: now + cooldownSeconds * 1000,
  }
  startCooldownTimer()
  return invited
}

function startCooldownTimer(): void {
  if (cooldownTimer !== undefined) return
  cooldownTimer = window.setInterval(() => {
    const now = Date.now()
    cooldownClock.value = now
    const active = Object.fromEntries(
      Object.entries(partyInviteCooldownUntil.value)
        .filter(([, readyAt]) => readyAt > now),
    )
    partyInviteCooldownUntil.value = active
    if (Object.keys(active).length === 0) clearCooldownTimer()
  }, 1000)
}

function clearCooldownTimer(): void {
  if (cooldownTimer !== undefined) window.clearInterval(cooldownTimer)
  cooldownTimer = undefined
}

function openFriendInvites(): void {
  openFriendsSurface()
}

function reopenPartyInvite(inviteID: string): void {
  if (!visibleIncomingPartyInvites.value.some(invite => invite.id === inviteID)) return
  dismissedPartyInviteIDs.value = new Set(
    visibleIncomingPartyInvites.value
      .map(invite => invite.id)
      .filter(currentID => currentID !== inviteID),
  )
}

async function openDirectMessage(
  steamID: string,
  trigger: HTMLElement | null,
): Promise<boolean> {
  const friend = sortedFriends.value.find(item => item.player.steam_id === steamID)
  if (!friend || !runtime.selectDirectChat(friend.player)) return false
  if (props.externalSurfaceOpen) emit('closeExternalSurface')
  chatDraft.value = ''
  if (!returnFocusTarget) {
    returnFocusTarget = (
      dockRoot.value?.querySelector<HTMLElement>('.party-strip-slot:not(:disabled)') ??
      trigger
    )
  }
  selectedPartyMemberSteamID.value = ''
  surface.value = 'chat'
  await nextTick()
  playerChat.value?.focusComposer()
  void runtime.markLatestMessageRead()
  return true
}

async function acceptPartyInvite(invite: PlayerPartyInvite): Promise<void> {
  if (rosterLocked.value || currentPartyInviteConflictReason.value) return
  const replacementNotice = currentPartyInviteReplacementNotice.value
  if (replacementNotice) {
    if (!window.confirm(appendQueueWarning(
      `Принять приглашение?\n\n${replacementNotice}`,
    ))) {
      return
    }
  } else if (!confirmQueuedRosterChange()) {
    return
  }
  await runtime.acceptPartyInvite(invite)
}

async function declinePartyInvite(inviteID: string): Promise<void> {
  await runtime.declinePartyInvite(inviteID)
}

async function declineAllPartyInvites(): Promise<void> {
  await runtime.declineAllPartyInvites()
}

function dismissPartyInvite(inviteID: string): void {
  runtime.clearPartyError()
  dismissedPartyInviteIDs.value = new Set([
    ...dismissedPartyInviteIDs.value,
    inviteID,
  ])
}

async function disbandParty(): Promise<void> {
  if (rosterLocked.value) return
  const party = runtime.party.value
  if (!party) return
  if (party.leader_steam_id !== props.player?.steam_id) return
  const message = party.members.length > 1
    ? 'Распустить группу для всех участников?'
    : 'Закрыть текущую группу?'
  const currentMatchWarning = props.matchmakerStatus === 'in_match'
    ? ' Чат группы и приглашения закроются, но состав текущего матча не изменится.'
    : ''
  if (!window.confirm(appendQueueWarning(`${message}${currentMatchWarning}`))) return
  if (await runtime.disbandParty()) closeSurface()
}

async function leaveParty(): Promise<void> {
  if (rosterLocked.value) return
  const party = runtime.party.value
  if (!party || party.leader_steam_id === props.player?.steam_id) return
  if (!window.confirm(appendQueueWarning('Покинуть текущую группу?'))) return
  if (await runtime.leaveParty()) closeSurface()
}

async function kickPartyMember(steamID: string): Promise<void> {
  if (rosterLocked.value) return
  const member = runtime.party.value?.members.find(candidate => candidate.steam_id === steamID)
  const name = member?.persona_name || steamID
  if (!window.confirm(appendQueueWarning(`Исключить ${name} из группы?`))) return
  const kicked = await runtime.kickPartyMember(steamID)
  if (kicked && selectedPartyMemberSteamID.value === steamID) {
    selectedPartyMemberSteamID.value = ''
    await nextTick()
    document.getElementById('player-party-panel')?.focus()
  }
}

async function transferPartyLeadership(steamID: string): Promise<void> {
  if (rosterLocked.value) return
  const member = runtime.party.value?.members.find(candidate => candidate.steam_id === steamID)
  if (!member) return
  const name = member.persona_name || steamID
  if (!window.confirm(appendQueueWarning(`Передать лидерство игроку ${name}?`))) return
  const transferred = await runtime.transferPartyLeadership(steamID)
  if (transferred && selectedPartyMemberSteamID.value === steamID) {
    await focusSelectedPartyMemberControl()
  }
}

async function sendChatMessage(): Promise<void> {
  const submittedDraft = chatDraft.value
  const submittedTargetKey = runtime.activeChatTargetKey.value
  if (
    recoveredDraftSourceTargetKey.value &&
    recoveredDraftSourceTargetKey.value !== submittedTargetKey &&
    !window.confirm(
      'Состав группы изменился после неудачной отправки. Отправить сохранённый текст в текущую группу?',
    )
  ) {
    return
  }
  recoveredDraftSourceTargetKey.value = null
  const sent = await runtime.sendMessage(submittedDraft)
  if (sent && chatDraft.value === submittedDraft) {
    chatDraft.value = ''
    return
  }
  if (
    !sent &&
    submittedTargetKey &&
    submittedDraft.trim() &&
    runtime.failedSendDraftTargetKey.value === submittedTargetKey
  ) {
    if (runtime.activeChatTargetKey.value !== submittedTargetKey) {
      chatDraft.value = submittedDraft
      recoveredDraftSourceTargetKey.value = submittedTargetKey
    }
    runtime.clearFailedSendDraftRecovery(submittedTargetKey)
  }
}

function confirmQueuedRosterChange(): boolean {
  if (props.matchmakerStatus !== 'queued') return true
  return window.confirm('Изменение состава отменит текущий поиск. Продолжить?')
}

function confirmLogicalSoloInviteDuringSearch(): boolean {
  if (props.matchmakerStatus !== 'queued' || runtime.party.value) return true
  return window.confirm(
    'Создание группы и отправка приглашения отменят текущий поиск. Продолжить?',
  )
}

function appendQueueWarning(message: string): string {
  return props.matchmakerStatus === 'queued'
    ? `${message}\n\nИзменение состава отменит текущий поиск.`
    : message
}

function profileSocialActionState(steamID: string): PlayerProfileSocialActionState {
  const targetSteamID = steamID.trim()
  const currentSteamID = props.player?.steam_id ?? ''
  if (
    !props.authenticated ||
    !props.accessAllowed ||
    !targetSteamID ||
    targetSteamID === currentSteamID
  ) {
    return unavailablePlayerProfileSocialActionState()
  }

  const socialState = runtime.socialState.value
  const relationship = playerProfileRelationship(socialState, targetSteamID)
  const socialLoading = runtime.socialLoading.value || !socialState
  const party = runtime.party.value
  const partyLoading = runtime.partyLoading.value || !runtime.partyStateHealthy.value
  const busy = runtime.socialActionPending.value || runtime.partyActionPending.value
  const cooldown = partyInviteCooldowns.value[targetSteamID] ?? 0
  const invitePresentation = playerProfilePartyInvitePresentation({
    party,
    actorSteamID: currentSteamID,
    targetSteamID,
    loading: partyLoading,
    actionPending: runtime.partyActionPending.value,
    targetBlocked: relationship.action === 'blocked',
    rosterLocked: rosterLocked.value,
    rosterLockReason: rosterLockReason.value,
    cooldownSeconds: cooldown,
    searchQueued: props.matchmakerStatus === 'queued',
  })

  const acceptedFriend = relationship.action === 'remove'
  const notificationIgnore = socialState?.notification_ignores?.find(
    ignore => ignore.player.steam_id === targetSteamID,
  ) ?? null
  const notificationIgnored = Boolean(notificationIgnore)
  const ignoreAction = playerProfileIgnoreAction(
    relationship.action,
    notificationIgnored,
  )
  let messageLabel = 'Написать сообщение'
  if (!resolvedChatCapabilities.value.dm) {
    messageLabel = 'Личные сообщения недоступны'
  } else if (!acceptedFriend) {
    messageLabel = 'Личные сообщения доступны друзьям'
  } else if (runtime.socialActionPending.value) {
    messageLabel = 'Социальное действие выполняется'
  }

  const friendLabel = {
    add: 'Добавить в друзья',
    accept: 'Принять заявку в друзья',
    cancel: 'Отменить заявку в друзья',
    remove: 'Удалить из друзей',
    blocked: 'Разблокировать игрока',
  }[relationship.action]

  return {
    visible: true,
    loading: socialLoading || partyLoading,
    busy,
    canInvite: (
      !socialLoading &&
      !busy &&
      invitePresentation.allowed
    ),
    canMessage: (
      !socialLoading &&
      !runtime.socialActionPending.value &&
      acceptedFriend &&
      resolvedChatCapabilities.value.dm
    ),
    showFriendAction: true,
    friendAction: relationship.action,
    friendActionDisabled: (
      socialLoading ||
      runtime.socialActionPending.value
    ),
    ignoreAction,
    ignoreActionDisabled: (
      socialLoading ||
      runtime.socialActionPending.value ||
      ignoreAction === null
    ),
    inviteLabel: invitePresentation.label,
    messageLabel,
    friendLabel,
    ignoreLabel: notificationIgnored ? 'Снять игнор' : 'Игнорировать',
    error: profileFeedbackSteamID.value === targetSteamID
      ? profileActionError.value
      : null,
  }
}

function profileSocialStatus(
  steamID: string,
): PlayerSocialStatusPresentation | null {
  const targetSteamID = steamID.trim()
  const currentSteamID = props.player?.steam_id ?? ''
  const socialState = runtime.socialState.value
  if (
    !props.authenticated ||
    !props.accessAllowed ||
    !targetSteamID ||
    !currentSteamID ||
    !socialState
  ) {
    return null
  }
  return playerProfileSocialStatus(socialState, currentSteamID, targetSteamID)
}

async function inviteProfilePlayer(steamID: string): Promise<boolean> {
  const targetSteamID = steamID.trim()
  if (!profileSocialActionState(targetSteamID).canInvite) return false
  beginProfileSocialAction(targetSteamID)
  const invited = await inviteFriend(targetSteamID)
  finishProfileSocialAction(
    targetSteamID,
    invited,
    runtime.partyError.value,
  )
  return invited
}

async function messageProfilePlayer(
  steamID: string,
  trigger: HTMLElement | null,
): Promise<boolean> {
  const targetSteamID = steamID.trim()
  if (!profileSocialActionState(targetSteamID).canMessage) return false
  beginProfileSocialAction(targetSteamID)
  const opened = await openDirectMessage(targetSteamID, trigger)
  finishProfileSocialAction(targetSteamID, opened, runtime.socialError.value)
  return opened
}

async function applyProfileFriendAction(
  steamID: string,
): Promise<PlayerProfileFriendAction | null> {
  const targetSteamID = steamID.trim()
  const state = profileSocialActionState(targetSteamID)
  if (state.friendActionDisabled) return null
  const relationship = playerProfileRelationship(
    runtime.socialState.value,
    targetSteamID,
  )
  beginProfileSocialAction(targetSteamID)
  let changed = false
  switch (relationship.action) {
    case 'add':
      changed = await runtime.sendFriendRequest(targetSteamID)
      break
    case 'accept':
      changed = await runtime.acceptFriendRequest(relationship.resourceID)
      break
    case 'cancel':
      changed = await runtime.removeFriendRequest(relationship.resourceID)
      break
    case 'remove':
      changed = await runtime.removeFriendship(relationship.resourceID)
      break
    case 'blocked':
      changed = await runtime.unblockPlayer(relationship.resourceID)
      break
  }
  finishProfileSocialAction(
    targetSteamID,
    changed,
    runtime.socialError.value,
  )
  return changed ? relationship.action : null
}

async function applyProfileIgnoreAction(
  steamID: string,
): Promise<PlayerProfileIgnoreAction | null> {
  const targetSteamID = steamID.trim()
  const state = profileSocialActionState(targetSteamID)
  if (state.ignoreActionDisabled || !state.ignoreAction) return null
  const notificationIgnore = runtime.socialState.value?.notification_ignores?.find(
    ignore => ignore.player.steam_id === targetSteamID,
  ) ?? null
  beginProfileSocialAction(targetSteamID)
  const changed = state.ignoreAction === 'ignore'
    ? await ignorePlayer(targetSteamID)
    : notificationIgnore
      ? await runtime.unignorePlayerNotifications(notificationIgnore.id)
      : false
  finishProfileSocialAction(
    targetSteamID,
    changed,
    runtime.socialError.value,
  )
  return changed ? state.ignoreAction : null
}

async function ignorePlayer(steamID: string): Promise<boolean> {
  return runtime.ignorePlayerNotifications(steamID)
}

function beginProfileSocialAction(steamID: string): void {
  runtime.clearSocialError()
  runtime.clearPartyError()
  profileFeedbackSteamID.value = steamID
  profileActionError.value = null
}

function finishProfileSocialAction(
  steamID: string,
  succeeded: boolean,
  error: string | null,
): void {
  if (profileFeedbackSteamID.value !== steamID) return
  profileActionError.value = succeeded ? null : error
}

function clearProfileSocialFeedback(): void {
  runtime.clearSocialError()
  runtime.clearPartyError()
  profileFeedbackSteamID.value = ''
  profileActionError.value = null
}

defineExpose<PlayerProfileSocialActionsController>({
  stateForPlayer: profileSocialActionState,
  statusForPlayer: profileSocialStatus,
  invitePlayer: inviteProfilePlayer,
  messagePlayer: messageProfilePlayer,
  applyFriendAction: applyProfileFriendAction,
  applyIgnoreAction: applyProfileIgnoreAction,
  clearFeedback: clearProfileSocialFeedback,
})

function reportChatMessage(
  message: PlayerChatMessage,
  reason: 'spam' | 'harassment' | 'abuse' | 'other',
): void {
  void runtime.reportChatMessage(message, reason)
}
</script>

<template>
  <div
    v-show="!hidden"
    ref="dockRoot"
    class="player-dock"
    :class="{
      guest: !authenticated,
      restricted: authenticated && !accessAllowed,
      'surface-open': surface !== 'none',
      'chat-open': surface === 'chat',
      'stream-online': runtime.streamConnected.value,
      'external-surface-open': externalSurfaceOpen,
      searching: matchmakerStatus === 'queued',
    }"
  >
    <button
      v-if="surface === 'friends' || surface === 'party'"
      class="player-dock-backdrop"
      type="button"
      aria-label="Закрыть панель"
      aria-hidden="true"
      tabindex="-1"
      @click="closeSurface()"
    ></button>

    <div
      v-if="authenticated && accessAllowed"
      class="player-dock-party-zone"
      :inert="dockModalOpen || externalSurfaceOpen"
    >
      <PartyStrip
        :authenticated="authenticated"
        :player="player"
        :party="runtime.party.value"
        :loading="runtime.partyLoading.value"
        :open="surface === 'party'"
        :friends-open="surface === 'friends'"
        :selected-member-steam-id="selectedPartyMemberSteamID"
        :tooltips-disabled="
          hidden ||
          dockModalOpen ||
          surface === 'chat' ||
          externalSurfaceOpen ||
          navigationSurfaceOpen
        "
        @open-profile="openPlayerProfile"
        @open-group="openPartyMember"
        @open-friends="openFriendsSurface"
      />
    </div>

    <PlayerChat
      v-if="authenticated && accessAllowed"
      ref="playerChat"
      v-model:draft="chatDraft"
      :authenticated="authenticated"
      :capabilities="resolvedChatCapabilities"
      :communication-restrictions="communicationRestrictions"
      :synchronized="runtime.partyStateHealthy.value"
      :party="runtime.party.value"
      :current-steam-id="player?.steam_id ?? ''"
      :active-kind="runtime.activeChatKind.value"
      :selected-peer="runtime.selectedDM.value"
      :recent-direct-conversations="runtime.recentDMConversations.value"
      :group-unread-count="groupUnreadCount"
      :collapsed-unread-count="collapsedChatUnreadCount"
      :global-has-unread="globalChatHasUnread"
      :friends="sortedFriends"
      :friends-loading="runtime.socialLoading.value"
      :friends-error="runtime.socialError.value"
      :conversation="runtime.conversation.value"
      :messages="runtime.messages.value"
      :loading="runtime.chatLoading.value"
      :sending="runtime.chatSending.value"
      :action-pending="runtime.socialActionPending.value"
      :error="runtime.chatError.value"
      :notice="runtime.chatNotice.value"
      :connected="runtime.streamConnected.value"
      :open="surface === 'chat'"
      :inert="dockModalOpen || externalSurfaceOpen"
      @open="trigger => toggleSurface('chat', trigger)"
      @close="closeSurface()"
      @send="sendChatMessage"
      @read="runtime.markLatestMessageRead"
      @report="reportChatMessage"
      @select-channel="runtime.selectChatChannel"
      @select-direct="runtime.selectDirectChat"
      @remove-direct="runtime.hideDirectChatFromRecents"
      @open-profile="openPlayerProfile"
      @retry-friends="runtime.refreshSocial"
      @clear-feedback="runtime.clearChatFeedback"
    />
    <PlayerVerificationNotice
      v-else-if="authenticated"
      :contact-url="verificationContactUrl"
      :cleanup-label="restrictedPartyCleanupLabel"
      :cleanup-busy="restrictedPartyCleanupBusy"
      :cleanup-error="restrictedPartyCleanupError"
      @cleanup="cleanupRestrictedParty"
    />

    <slot name="external-surface"></slot>

    <div class="player-dock-play-zone" :inert="dockModalOpen">
      <slot name="play"></slot>
    </div>

    <Transition name="player-dock-panel">
      <PlayerFriendsPanel
        v-if="surface === 'friends' && player"
        :friends="sortedFriends"
        :outgoing-requests="outgoingFriendRequests"
        :party="runtime.party.value"
        :current-steam-id="player.steam_id"
        :party-invite-cooldowns="partyInviteCooldowns"
        :party-invite-locked="rosterLocked"
        :party-invite-lock-reason="rosterLockReason"
        :loading="runtime.socialLoading.value"
        :action-pending="runtime.socialActionPending.value || runtime.partyActionPending.value"
        :error="runtime.socialError.value || runtime.partyError.value"
        :inert="addFriendDialogOpen"
        :aria-hidden="addFriendDialogOpen ? 'true' : undefined"
        @close="closeSurface()"
        @add-friend="openAddFriendDialog"
        @open-profile="openPlayerProfile"
        @message="openDirectMessage"
        @invite="inviteFriend"
        @remove-request="runtime.removeFriendRequest"
        @clear-error="runtime.clearSocialError(); runtime.clearPartyError()"
      />
    </Transition>

    <Transition name="player-dock-panel">
      <PlayerPartyPanel
        v-if="surface === 'party' && player"
        :player="player"
        :party="runtime.party.value"
        :loading="runtime.partyLoading.value"
        :action-pending="runtime.partyActionPending.value"
        :roster-locked="rosterLocked"
        :roster-lock-reason="rosterLockReason"
        :selected-member-steam-id="selectedPartyMemberSteamID"
        :message-enabled="selectedPartyMemberMessageState.enabled"
        :message-reason="selectedPartyMemberMessageState.reason"
        :error="runtime.partyError.value"
        @close="closeSurface()"
        @invite="openFriendInvites"
        @disband="disbandParty"
        @leave="leaveParty"
        @select="selectPartyMember"
        @open-profile="openPlayerProfile"
        @message="openDirectMessage"
        @kick="kickPartyMember"
        @transfer="transferPartyLeadership"
        @clear-error="runtime.clearPartyError"
      />
    </Transition>

    <Transition name="player-dock-panel">
      <PlayerAddFriendDialog
        v-if="addFriendDialogOpen && player"
        v-model:draft="friendRequestDraft"
        :current-steam-id="player.steam_id"
        :action-pending="runtime.socialActionPending.value"
        :error="runtime.socialError.value"
        @close="closeAddFriendDialog()"
        @submit="submitFriendRequest"
        @clear-error="runtime.clearSocialError"
      />
    </Transition>

    <Teleport v-if="authenticated && accessAllowed" to="#player-notification-host">
      <PlayerNotificationCenter
        :requests="incomingFriendRequests"
        :party-invites="visibleIncomingPartyInvites"
        :party-invite-count="runtime.incomingPartyInvites.value.length"
        :loading="runtime.socialLoading.value"
        :action-pending="runtime.socialActionPending.value || runtime.partyActionPending.value"
        :error="runtime.socialError.value || runtime.partyError.value"
        :disabled="hidden || externalSurfaceOpen || dockModalOpen"
        :close-requested="navigationSurfaceOpen"
        @accept="runtime.acceptFriendRequest"
        @decline="runtime.removeFriendRequest"
        @ignore-player="ignorePlayer"
        @open-party-invite="reopenPartyInvite"
        @decline-all-party-invites="declineAllPartyInvites"
        @opened="$emit('closeExternalSurface')"
        @clear-error="runtime.clearSocialError(); runtime.clearPartyError()"
      />
    </Teleport>

    <Transition name="player-party-invite">
      <PlayerPartyInviteDialog
        v-if="activePartyInvite"
        :invite="activePartyInvite"
        :action-pending="runtime.partyActionPending.value || runtime.socialActionPending.value"
        :accept-disabled="partyInviteAcceptDisabled"
        :accept-disabled-reason="partyInviteAcceptDisabledReason"
        :accept-notice="currentPartyInviteReplacementNotice"
        :error="runtime.partyError.value || runtime.socialError.value"
        @accept="acceptPartyInvite"
        @decline="declinePartyInvite"
        @ignore-player="ignorePlayer"
        @dismiss="dismissPartyInvite"
        @clear-error="runtime.clearPartyError(); runtime.clearSocialError()"
      />
    </Transition>
  </div>
</template>
